/**
 * useAIPanel — fetch the AI-composed PanelSpec for a (workspace, page,
 * instance). Returns null when no spec is composed; the caller falls
 * back to the host-app default.
 *
 * Pairs with the compose_panel MCP tool: when the AI composes a spec
 * it writes to workspaces.ai_panels[page_kind][instance_id] and
 * broadcasts ai_panels via BrowserBus. The useBrowserBus listener
 * invalidates the matching queryKey ("ai-panel" prefix) so the page
 * refetches without a manual reload.
 */

import { useQuery } from "@tanstack/react-query";
import type { PanelSpec } from "../../framework/react/PanelRenderer.js";

export function useAIPanel(
  workspaceId: string | null | undefined,
  pageKind: string,
  instanceId: string = "default"
) {
  return useQuery<{ spec: PanelSpec | null }>({
    queryKey: ["ai-panel", workspaceId, pageKind, instanceId],
    queryFn: async () => {
      if (!workspaceId) return { spec: null };
      const res = await fetch(
        `/api/v1/workspaces/${encodeURIComponent(workspaceId)}/panels?page_kind=${encodeURIComponent(pageKind)}&instance_id=${encodeURIComponent(instanceId)}`,
        { credentials: "include" }
      );
      if (!res.ok) throw new Error("Could not load this page. Check your workspace access and try again.");
      return res.json();
    },
    enabled: !!workspaceId,
    // Stale while revalidate — the broadcast invalidation handles
    // freshness when the AI composes a new panel.
    staleTime: 30_000,
  });
}
